/**
 * TextBlock — a free-form content screen (server contract:
 * `agent-dev-server/src/bl/builtin-catalog/text-block.ts`).
 *
 * Header via the shared SurfaceHeader, prose via MarkdownText, plus an
 * ordered `blocks` array the model composes freely: headings, text passages,
 * images, buttons — any order, any count. Consecutive buttons group onto one
 * flex row.
 */
import type { FC, ReactNode } from 'react';
import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { MarkdownText } from '../blocks/MarkdownText.tsx';
import { SurfaceHeader } from '../blocks/SurfaceHeader.tsx';
import { useSendIntent } from '../blocks/use-send-intent.ts';
import { isRecord } from '../../util/type-guards.ts';
import { arr, optStr, str } from '../props.ts';
import { SuggestedReply } from '../blocks/SuggestedReply.tsx';
import { ExploreContent } from '../ExploreContent.tsx';

interface ContentBlock {
  type: 'heading' | 'text' | 'image' | 'button';
  text?: string;
  imageUrl?: string;
  imageAlt?: string;
  caption?: string;
  label?: string;
  intent?: string;
}

function toBlock(value: unknown): ContentBlock | null {
  if (!isRecord(value)) {
    return null;
  }
  const type = str(value.type);
  if (type !== 'heading' && type !== 'text' && type !== 'image' && type !== 'button') {
    return null;
  }
  return {
    type,
    text: optStr(value.text),
    imageUrl: optStr(value.imageUrl),
    imageAlt: optStr(value.imageAlt),
    caption: optStr(value.caption),
    label: optStr(value.label),
    intent: optStr(value.intent),
  };
}

/** Group consecutive button blocks so they share one flex row. */
function toSegments(blocks: ContentBlock[]): (ContentBlock | ContentBlock[])[] {
  const segments: (ContentBlock | ContentBlock[])[] = [];
  for (const block of blocks) {
    const last = segments[segments.length - 1];
    if (block.type === 'button' && Array.isArray(last)) {
      last.push(block);
    } else if (block.type === 'button') {
      segments.push([block]);
    } else {
      segments.push(block);
    }
  }
  return segments;
}

export const TextBlockSurface: FC<A2uiNodeViewProps> = ({ node }) => {
  const sendIntent = useSendIntent();
  const title = optStr(node.props.highlight ?? node.props.title);
  const legacySubtitle = 'message' in node.props ? undefined : optStr(node.props.subtitle);
  const body = optStr(node.props.message ?? node.props.body);
  const blocks = arr(node.props.blocks)
    .map(toBlock)
    .filter((block): block is ContentBlock => block !== null);

  if (!title && !legacySubtitle && !body && blocks.length === 0) {
    return null;
  }

  const renderSegment = (segment: ContentBlock | ContentBlock[], index: number): ReactNode => {
    if (Array.isArray(segment)) {
      return (
        <div key={index} className="my-6 flex w-full flex-wrap items-start gap-2">
          {segment.map((button) =>
            button.label && button.intent ? (
              <SuggestedReply
                key={button.label}
                type="button"
                className="pointer-events-auto"
                onClick={() => sendIntent(button.intent ?? '')}
              >
                {button.label}
              </SuggestedReply>
            ) : null,
          )}
        </div>
      );
    }
    if (segment.type === 'heading' && segment.text) {
      return (
        <h2
          key={index}
          className="mt-8 mb-3 w-full text-2xl font-semibold tracking-tight text-foreground"
        >
          {segment.text}
        </h2>
      );
    }
    if (segment.type === 'text' && segment.text) {
      const isIntroLine =
        (Boolean(title) && index === 0) ||
        (!title && index === 1 && blocks[0]?.type === 'heading');
      return (
        <div key={index} className="w-full max-w-prose">
          <MarkdownText
            text={segment.text}
            className={isIntroLine ? 'stage-intro-copy' : undefined}
          />
        </div>
      );
    }

    if (segment.type === 'image' && segment.imageUrl) {
      return (
        <ExploreContent
          key={index}
          node={node}
          selection={{
            imageUrl: segment.imageUrl,
            caption: segment.caption,
            alt: segment.imageAlt,
          }}
          subject={segment.imageAlt ?? segment.caption ?? title ?? ''}
          className="my-6 w-full"
        >
          <figure>
            <div className="overflow-hidden rounded-lg border border-border">
              <img
                src={segment.imageUrl}
                alt={segment.imageAlt ?? segment.caption ?? ''}
                loading="lazy"
                className="w-full object-cover"
              />
            </div>
            {segment.caption ? (
              <figcaption className="mt-2 text-center text-xs text-muted-foreground">
                {segment.caption}
              </figcaption>
            ) : null}
          </figure>
        </ExploreContent>
      );
    }
    return null;
  };

  return (
    <div className="w-full min-w-0">
      <div className="w-full max-w-prose">
        {title ? <SurfaceHeader title={title} /> : null}
        {legacySubtitle ? (
          <p className="stage-intro-line prose-body mb-2 text-foreground">{legacySubtitle}</p>
        ) : null}
        {body ? <MarkdownText text={body} className={title ? 'stage-intro-copy' : undefined} /> : null}
      </div>
      {toSegments(blocks).map(renderSegment)}
    </div>
  );
};
